import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "../../hooks/useTranslation";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_PLACES_KEY;

// Debug: Log API key status (only in development)
if (process.env.NODE_ENV === 'development') {
  console.log('Google Maps API Key Status:', GOOGLE_MAPS_API_KEY ? 'Present' : 'Missing');
}


const AddressAutocomplete = ({ onSelectAddress, selectedAddress }) => {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const searchInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const [isSelectingLocation, setIsSelectingLocation] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Load Google Maps script with Places and Geocoding libraries
  useEffect(() => {
    // Check if API key is available
    if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY.trim() === "") {
      console.error(
        "Google Maps API key is missing. Please set REACT_APP_GOOGLE_MAPS_API_PLACES_KEY in your .env file"
      );
      setError("Google Maps API key is missing. Please contact administrator.");
      setIsLoading(false);
      return;
    }

    const hasMaps = !!(window.google && window.google.maps);
    const hasPlaces = !!(window.google && window.google.maps && window.google.maps.places);
    const hasGeocoder = !!(window.google && window.google.maps && window.google.maps.Geocoder);

    // If everything we need is already available, just mark as loaded
    if (hasMaps && hasPlaces && hasGeocoder) {
      setIsScriptLoaded(true);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Always ensure our script with Places + Geometry is loaded.
    // It's safe to add another script tag even if one without libraries already exists.
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,geometry&language=en`;
    script.async = true;
    script.defer = true;

    // Set a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        setError(
          "Google Maps failed to load required libraries. Please check your internet connection and API key."
        );
      }
    }, 15000); // 15 second timeout

    script.onload = () => {
      if (window.google && window.google.maps && window.google.maps.places && window.google.maps.Geocoder) {
        setIsScriptLoaded(true);
        setIsLoading(false);
        setError(null);
      } else {
        setError("Google Maps loaded but some required libraries are missing.");
        setIsLoading(false);
      }
      clearTimeout(timeoutId);
    };

    script.onerror = () => {
      console.error("Failed to load Google Maps script");
      setError(
        "Failed to load Google Maps. Please check your API key and internet connection."
      );
      setIsLoading(false);
      clearTimeout(timeoutId);
    };

    document.head.appendChild(script);

    // Cleanup: we keep the script tag so that subsequent openings don't reload it
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  // Initialize map when modal opens
  useEffect(() => {
    if (!isMapModalOpen || !isScriptLoaded || !mapRef.current) return;

    // Chhota sa delay taake modal ka layout / size settle ho jaye
    const timer = setTimeout(() => {
      try {
        if (!window.google || !window.google.maps) {
          console.warn("Google Maps not available on modal open");
          return;
        }

        // Convert coordinates to numbers if they're strings
        const getLocation = () => {
          if (selectedLocation && selectedLocation.latitude && selectedLocation.longitude) {
            return {
              lat: parseFloat(selectedLocation.latitude),
              lng: parseFloat(selectedLocation.longitude),
            };
          }
          return null;
        };

        const initialLocation = getLocation();

        // Initialize map
        const map = new window.google.maps.Map(mapRef.current, {
          center: initialLocation || { lat: 0, lng: 0 },
          zoom: initialLocation ? 15 : 2,
          mapTypeControl: true,
          streetViewControl: true,
        });

        mapInstanceRef.current = map;

        // Initialize geocoder
        geocoderRef.current = new window.google.maps.Geocoder();

        // Initialize Places Autocomplete for search
        if (searchInputRef.current && window.google.maps.places) {
          autocompleteRef.current = new window.google.maps.places.Autocomplete(
            searchInputRef.current,
            {
              types: ["geocode", "establishment"],
              fields: ["formatted_address", "geometry", "name", "place_id"],
            }
          );

          // Handle place selection from search
          autocompleteRef.current.addListener("place_changed", () => {
            const place = autocompleteRef.current.getPlace();

            if (!place || !place.geometry) {
              return;
            }

            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            const address = place.formatted_address || place.name || "";

            // Center map on selected place
            map.setCenter({ lat, lng });
            map.setZoom(15);

            // Remove existing marker
            if (markerRef.current) {
              markerRef.current.setMap(null);
            }

            // Add new marker
            markerRef.current = new window.google.maps.Marker({
              position: { lat, lng },
              map: map,
              draggable: true,
              animation: window.google.maps.Animation.DROP,
            });

            // Update location
            const locationData = {
              address,
              latitude: lat,
              longitude: lng,
            };
            setSelectedLocation(locationData);
            // Reflect in search input
            if (searchInputRef.current) {
              searchInputRef.current.value = address;
            }

            if (onSelectAddress) {
              onSelectAddress(locationData);
            }

            // Update marker position when dragged
            markerRef.current.addListener("dragend", (e) => {
              const newLat = e.latLng.lat();
              const newLng = e.latLng.lng();

              setIsSelectingLocation(true);

              geocoderRef.current.geocode(
                { location: { lat: newLat, lng: newLng } },
                (results, status) => {
                  setIsSelectingLocation(false);
                  if (status === "OK" && results[0]) {
                    const newAddress = results[0].formatted_address;
                    const newLocationData = {
                      address: newAddress,
                      latitude: newLat,
                      longitude: newLng,
                    };
                    setSelectedLocation(newLocationData);
                    if (searchInputRef.current) {
                      searchInputRef.current.value = newAddress;
                    }

                    if (onSelectAddress) {
                      onSelectAddress(newLocationData);
                    }
                  }
                }
              );
            });
          });
        }

        // Try to get user's current location
        if (navigator.geolocation && !initialLocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const userLocation = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              };
              map.setCenter(userLocation);
              map.setZoom(15);
            },
            (error) => {
              console.warn("Error getting user location:", error);
              // Default to a common location if geolocation fails
              map.setCenter({ lat: 40.7128, lng: -74.006 }); // New York
              map.setZoom(10);
            }
          );
        } else if (initialLocation) {
          // Add marker for previously selected location
          markerRef.current = new window.google.maps.Marker({
            position: initialLocation,
            map: map,
            draggable: true,
          });
        }

        // Add click listener to map
        map.addListener("click", (e) => {
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();

          setIsSelectingLocation(true);

          // Remove existing marker if any
          if (markerRef.current) {
            markerRef.current.setMap(null);
          }

          // Add new marker
          markerRef.current = new window.google.maps.Marker({
            position: { lat, lng },
            map: map,
            draggable: true,
            animation: window.google.maps.Animation.DROP,
          });

          // Ensure geocoder is initialized
          if (!geocoderRef.current) {
            geocoderRef.current = new window.google.maps.Geocoder();
          }

          // Reverse geocode to get address
          geocoderRef.current.geocode(
            { location: { lat, lng } },
            (results, status) => {
              setIsSelectingLocation(false);

              if (
                status === "OK" &&
                results &&
                Array.isArray(results) &&
                results.length > 0 &&
                results[0]
              ) {
                const address = results[0].formatted_address;

                if (address) {
                  const locationData = {
                    address,
                    latitude: lat,
                    longitude: lng,
                  };
                  setSelectedLocation(locationData);
                  if (searchInputRef.current) {
                    searchInputRef.current.value = address;
                  }

                  if (onSelectAddress) {
                    onSelectAddress(locationData);
                  }
                } else {
                  // Address field is empty, try to construct from components
                  const components = results[0].address_components;
                  if (components && components.length > 0) {
                    const addressParts = components
                      .map((comp) => comp.long_name)
                      .filter(Boolean);
                    const constructedAddress =
                      addressParts.join(", ") ||
                      `${t("mapPicker").locationAt} ${lat.toFixed(
                        6
                      )}, ${lng.toFixed(6)}`;
                    const locationData = {
                      address: constructedAddress,
                      latitude: lat,
                      longitude: lng,
                    };
                    setSelectedLocation(locationData);
                    if (searchInputRef.current) {
                      searchInputRef.current.value = constructedAddress;
                    }

                    if (onSelectAddress) {
                      onSelectAddress(locationData);
                    }
                  } else {
                    // No address components available
                    const address = `${t("mapPicker").locationAt} ${lat.toFixed(
                      6
                    )}, ${lng.toFixed(6)}`;
                    const locationData = {
                      address,
                      latitude: lat,
                      longitude: lng,
                    };
                    setSelectedLocation(locationData);
                    if (searchInputRef.current) {
                      searchInputRef.current.value = address;
                    }

                    if (onSelectAddress) {
                      onSelectAddress(locationData);
                    }
                  }
                }
              } else {
                // Geocoding failed - log the error and use coordinates
                console.warn("Geocoding failed for location:", {
                  lat,
                  lng,
                  status,
                  results,
                });
                const address = `${t("mapPicker").locationAt} ${lat.toFixed(
                  6
                )}, ${lng.toFixed(6)}`;
                const locationData = {
                  address,
                  latitude: lat,
                  longitude: lng,
                };
                setSelectedLocation(locationData);
                if (searchInputRef.current) {
                  searchInputRef.current.value = address;
                }

                if (onSelectAddress) {
                  onSelectAddress(locationData);
                }
              }
            }
          );

          // Update marker position when dragged
          markerRef.current.addListener("dragend", (e) => {
            const newLat = e.latLng.lat();
            const newLng = e.latLng.lng();

            setIsSelectingLocation(true);

            geocoderRef.current.geocode(
              { location: { lat: newLat, lng: newLng } },
              (results, status) => {
                setIsSelectingLocation(false);
                if (status === "OK" && results[0]) {
                  const address = results[0].formatted_address;
                  const locationData = {
                    address,
                    latitude: newLat,
                    longitude: newLng,
                  };
                  setSelectedLocation(locationData);
                  if (searchInputRef.current) {
                    searchInputRef.current.value = address;
                  }

                  if (onSelectAddress) {
                    onSelectAddress(locationData);
                  }
                }
              }
            );
          });
        });

        setError(null);
      } catch (err) {
        console.error("Error initializing map:", err);
        setError(t("mapPicker").mapInitializationError);
      }
    }, 200);

    // Cleanup
    return () => {
      clearTimeout(timer);
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
      if (autocompleteRef.current && window.google && window.google.maps) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [isMapModalOpen, isScriptLoaded]);

  // Fix for map not rendering correctly when modal opens
  // Sometimes Google Maps needs a manual resize trigger when shown inside a modal
  useEffect(() => {
    if (!isMapModalOpen) return;
    if (!mapInstanceRef.current) return;
    if (!window.google || !window.google.maps) return;

    // Small timeout so modal layout can settle
    const timer = setTimeout(() => {
      try {
        window.google.maps.event.trigger(mapInstanceRef.current, "resize");

        // Re-center after resize so tiles load correctly
        if (selectedLocation?.latitude && selectedLocation?.longitude) {
          mapInstanceRef.current.setCenter({
            lat: parseFloat(selectedLocation.latitude),
            lng: parseFloat(selectedLocation.longitude),
          });
        }
      } catch (e) {
        console.warn("Google Maps resize on modal open failed:", e);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [isMapModalOpen, selectedLocation]);

  // Check if the address is actually coordinates (not a real address)
  const isCoordinates = (addr) => {
    if (!addr) return false;
    // Check if it matches coordinate pattern like "40.7128, -74.0060" or "40.7128,-74.0060"
    const coordinatePattern = /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/;
    return coordinatePattern.test(addr.trim());
  };

  // Update selected location when prop changes
  useEffect(() => {
    if (selectedAddress) {
      const addr = selectedAddress.address;
      
      // If address is actually coordinates, try to reverse geocode to get real address
      if (isCoordinates(addr) && selectedAddress.latitude && selectedAddress.longitude && isScriptLoaded && window.google && window.google.maps) {
        // Initialize geocoder if not already initialized
        if (!geocoderRef.current) {
          geocoderRef.current = new window.google.maps.Geocoder();
        }
        
        const lat = parseFloat(selectedAddress.latitude);
        const lng = parseFloat(selectedAddress.longitude);
        
        geocoderRef.current.geocode(
          { location: { lat, lng } },
          (results, status) => {
            if (status === 'OK' && results[0]) {
              setSelectedLocation({
                address: results[0].formatted_address,
                latitude: lat,
                longitude: lng,
              });
            } else {
              // Keep coordinates if geocoding fails, but don't show as address
              setSelectedLocation({
                address: addr, // Will be filtered out in display
                latitude: lat,
                longitude: lng,
              });
            }
          }
        );
      } else {
        // Normal address, just set it
        setSelectedLocation({
          address: selectedAddress.address,
          latitude: selectedAddress.latitude,
          longitude: selectedAddress.longitude,
        });
      }
    }
  }, [selectedAddress, isScriptLoaded]);

  const handleOpenMap = () => {
    setIsMapModalOpen(true);
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
  };

  const handleCloseMap = () => {
    setIsMapModalOpen(false);
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
  };

  const handleConfirmLocation = () => {
    if (selectedLocation && onSelectAddress) {
      onSelectAddress(selectedLocation);
    }
    setIsMapModalOpen(false);
  };

  if (isLoading && !error) {
    return (
      <div className="border border-[#DDDDDD] rounded-[10px] p-[15px] w-full flex items-center gap-2">
        <div className="w-4 h-4 border-2 border-[#4CAF50] border-t-transparent rounded-full animate-spin"></div>
        <span className="text-sm text-gray-500">{t("mapPicker").loadingMap}</span>
      </div>
    );
  }

  if (error && !isScriptLoaded) {
    return (
      <div className="border border-red-300 rounded-[10px] p-[15px] w-full">
        <div className="flex items-center gap-2 text-red-600">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span className="text-sm font-medium">{error}</span>
        </div>
        <p className="text-xs text-gray-600 mt-2">
          {!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY.trim() === '' 
            ? 'Please set REACT_APP_GOOGLE_MAPS_API_PLACES_KEY in your .env file'
            : 'Please check your API key configuration or try refreshing the page'}
        </p>
      </div>
    );
  }

  const displayAddress = () => {
    if (!selectedLocation?.address) {
      return t("mapPicker").noAddressSelected;
    }
    // Don't show coordinates as address
    if (isCoordinates(selectedLocation.address)) {
      return t("mapPicker").addressNotAvailable;
    }
    return selectedLocation.address;
  };

  return (
    <div className="relative w-full">
      <div className="border border-[#DDDDDD] rounded-[10px] p-[15px] w-full flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className={`text-sm truncate ${
            selectedLocation?.address && !isCoordinates(selectedLocation.address)
              ? "text-gray-700" 
              : "text-gray-400"
          }`}>
            {displayAddress()}
          </p>
        </div>
        <button
          type="button"
          onClick={handleOpenMap}
          className="px-4 py-2 bg-[#4CAF50] text-white text-sm font-medium rounded-[8px] hover:bg-[#209b25] transition-colors whitespace-nowrap"
        >
          {t("mapPicker").findAddress}
        </button>
      </div>

      {error && (
        <p className="text-red-500 text-sm mt-1">{error}</p>
      )}

      {/* Map Modal */}
      {isMapModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[16px] w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex flex-col gap-3 p-4 border-b border-[#DDDDDD]">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{t("mapPicker").selectLocationOnMap}</h3>
                <button
                  onClick={handleCloseMap}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              
              {/* Search Input */}
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={t("mapPicker").searchPlaceholder}
                  className="w-full border border-[#DDDDDD] rounded-[10px] p-[12px] pr-10 focus:outline-none focus:border-[#4CAF50]"
                />
                <svg
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Map Container */}
            <div className="relative flex-1 min-h-[400px]">
              <div ref={mapRef} className="w-full h-full rounded-b-[16px]" style={{ minHeight: '400px' }} />
              
              {isSelectingLocation && (
                <div className="absolute top-4 left-4 bg-white px-4 py-2 rounded-lg shadow-md flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-[#4CAF50] border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-gray-700">{t("mapPicker").gettingAddress}</span>
                </div>
              )}

              {selectedLocation && !isSelectingLocation && (
                <div className="absolute top-4 left-4 bg-white px-4 py-2 rounded-lg shadow-md max-w-md">
                  <p className="text-sm font-medium text-gray-800">{t("mapPicker").selectedAddress}</p>
                  <p className="text-xs text-gray-600 mt-1">{selectedLocation.address}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t("mapPicker").coordinates} {parseFloat(selectedLocation.latitude).toFixed(6)}, {parseFloat(selectedLocation.longitude).toFixed(6)}
                  </p>
                </div>
              )}

              <div className="absolute bottom-4 left-4 bg-yellow-400 px-4 py-2 rounded-lg shadow-md">
                <p className="text-sm text-gray-800 font-medium">{t("mapPicker").clickToSelectLocation}</p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-[#DDDDDD]">
              <button
                onClick={handleCloseMap}
                className="px-4 py-2 border border-[#DDDDDD] rounded-[8px] text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t("mapPicker").cancel}
              </button>
              <button
                onClick={handleConfirmLocation}
                disabled={!selectedLocation}
                className="px-4 py-2 bg-[#4CAF50] text-white rounded-[8px] hover:bg-[#209b25] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {t("mapPicker").confirmLocation}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
